import { z } from 'zod';

/**
 * Memory taxonomy for the clinical conversational-memory system (ADR-075).
 *
 * The state vocabularies and the 13 core categories are frozen byte-identical
 * to the sister platform's ("Engram") core so a future cross-product store
 * merge is a data union, not a migration. Never rename or repurpose a core
 * category or state value; SUD categories are additive only, each with a
 * documented down-map to a core category (see `taxonomy.md`). These `as const`
 * arrays back both the Zod wire schemas and the text+CHECK constraints in
 * db/schema.ts (ADR-056 Rule 8), so the DB and the contract cannot drift.
 */

export const MEMORY_CATEGORY_CORE = [
  'identity',
  'presenting_problem',
  'diagnosis_history',
  'medication',
  'safety',
  'relationship',
  'life_circumstance',
  'life_event',
  'treatment',
  'coping',
  'pattern',
  'preference',
  'other',
] as const;
export type CoreMemoryCategory = (typeof MEMORY_CATEGORY_CORE)[number];

export const MEMORY_CATEGORY_SUD = ['substance_use', 'recovery_goal', 'lapse_relapse'] as const;
export type SudMemoryCategory = (typeof MEMORY_CATEGORY_SUD)[number];

export const MEMORY_CATEGORY = [...MEMORY_CATEGORY_CORE, ...MEMORY_CATEGORY_SUD] as const;
export const memoryCategorySchema = z.enum(MEMORY_CATEGORY);
export type MemoryCategory = z.infer<typeof memoryCategorySchema>;

/**
 * Every additive SUD category folds into exactly one core category, so a
 * core-only consumer (or a future merged store) can down-map rows without
 * loss of the frozen-core guarantee.
 */
export const SUD_CATEGORY_DOWNMAP: Record<SudMemoryCategory, CoreMemoryCategory> = {
  substance_use: 'pattern',
  recovery_goal: 'treatment',
  lapse_relapse: 'life_event',
};

export const MEMORY_CONFIDENCE_TIER = ['explicit', 'implied', 'inferred', 'speculative'] as const;
export const memoryConfidenceTierSchema = z.enum(MEMORY_CONFIDENCE_TIER);
export type MemoryConfidenceTier = z.infer<typeof memoryConfidenceTierSchema>;

/**
 * Tier strength follows the frozen array order (strongest first). Used by the
 * dedup corroboration path: a restatement at a stronger tier PROMOTES the
 * stored row (e.g. a speculative shadow row becomes retrievable when the
 * member states the fact explicitly) — corroboration never downgrades.
 */
export function isStrongerConfidence(a: MemoryConfidenceTier, b: MemoryConfidenceTier): boolean {
  return MEMORY_CONFIDENCE_TIER.indexOf(a) < MEMORY_CONFIDENCE_TIER.indexOf(b);
}

export const MEMORY_LIFECYCLE = ['active', 'archived', 'deleted'] as const;
export const memoryLifecycleSchema = z.enum(MEMORY_LIFECYCLE);
export type MemoryLifecycle = z.infer<typeof memoryLifecycleSchema>;

export const MEMORY_VERIFICATION = ['unverified', 'verified', 'disputed', 'superseded'] as const;
export const memoryVerificationSchema = z.enum(MEMORY_VERIFICATION);
export type MemoryVerification = z.infer<typeof memoryVerificationSchema>;

export const MEMORY_LINK_TYPE = [
  'supersedes',
  'contradicts',
  'corroborates',
  'elaborates',
  'consolidates',
  'derives_from',
] as const;
export const memoryLinkTypeSchema = z.enum(MEMORY_LINK_TYPE);
export type MemoryLinkType = z.infer<typeof memoryLinkTypeSchema>;

/**
 * Content authoring methodology, mirrored from the sister platform's
 * dual-pass pipeline: this slice writes only `narrative`; `bullet_predicate`
 * is carried in the frozen vocabulary so ported rows remain representable.
 */
export const MEMORY_METHODOLOGY = ['narrative', 'bullet_predicate'] as const;
export const memoryMethodologySchema = z.enum(MEMORY_METHODOLOGY);
export type MemoryMethodology = z.infer<typeof memoryMethodologySchema>;

/**
 * Categories that may only be stored at `explicit` confidence (clinical
 * safety gate, ADR-075 Rule 3). The sister core protects safety, medication,
 * diagnosis_history, and identity; Galapagos extends the same guarantee to
 * the SUD categories — this product's clinical core.
 */
export const EXPLICIT_ONLY_MEMORY_CATEGORIES = [
  'safety',
  'medication',
  'diagnosis_history',
  'identity',
  ...MEMORY_CATEGORY_SUD,
] as const satisfies readonly MemoryCategory[];

/**
 * Categories ranked and budget-protected first at retrieval (ADR-075 Rule 4).
 * Intentionally the same membership as the explicit-only set today — the two
 * serve different enforcement semantics (retrieval ranking vs extraction
 * gating) and may diverge, mirroring the sister platform's note.
 */
export const PROTECTED_MEMORY_CATEGORIES = EXPLICIT_ONLY_MEMORY_CATEGORIES;

export function isProtectedMemoryCategory(category: MemoryCategory): boolean {
  return PROTECTED_MEMORY_CATEGORIES.some((protectedCategory) => protectedCategory === category);
}

/**
 * Categories whose confidence is always forced to `inferred` regardless of
 * what extraction returns — patterns are observational by nature and must
 * never carry `explicit` confidence (ported safety-gate rule).
 */
export const FORCED_INFERRED_MEMORY_CATEGORIES = [
  'pattern',
] as const satisfies readonly MemoryCategory[];

/**
 * Stamped on every memory row so cross-product export/import stays cheap
 * (full portability tooling is Phase 2). Bump only with a documented shape
 * change in `taxonomy.md`.
 */
export const MEMORY_SCHEMA_VERSION = 1;

/**
 * The `memory_records.metadata` jsonb — additive extension room. Carries the
 * quote-or-drop audit trail (`sourceEvidence`, the verified member quote —
 * PHI-class, protected exactly like `content`: never in logs, signals, or
 * client payloads) and the `speculativeTtl` flag; everything else stays
 * counts/flags only.
 */
export const memoryRecordMetadataSchema = z.record(z.string(), z.unknown());
export type MemoryRecordMetadata = z.infer<typeof memoryRecordMetadataSchema>;

/**
 * The `extraction_state.outcome` jsonb — pipeline outcome accounting for one
 * conversation. Counts and outcome classes only, never memory content
 * (ADR-075 Rule 9 confinement applies to rows readable by operators too).
 */
export const extractionOutcomeSchema = z.object({
  candidateCount: z.number().int().nonnegative(),
  verificationDroppedCount: z.number().int().nonnegative(),
  safetyRejectedCount: z.number().int().nonnegative(),
  dedupCollisionCount: z.number().int().nonnegative(),
  storedCount: z.number().int().nonnegative(),
});
export type ExtractionOutcome = z.infer<typeof extractionOutcomeSchema>;

export const EXTRACTION_STATE_STATUS = [
  'processing',
  'completed',
  'skipped_unattributed',
  'failed',
] as const;
export const extractionStateStatusSchema = z.enum(EXTRACTION_STATE_STATUS);
export type ExtractionStateStatus = z.infer<typeof extractionStateStatusSchema>;
